// Централизованный расчёт балансов.
// Транзакции:
//   income/expense — обычные
//   transfer — перевод: accountId (откуда), toAccountId (куда)
//   debt — долг: accountId (реальный счёт), debtAccountId (счёт-человек), debtKind:
//        lend    — дал в долг   (деньги ушли, человек должен мне)
//        borrow  — взял в долг  (деньги пришли, я должен человеку)
//        return  — вернул долг  (деньги ушли, мой долг уменьшился)
//        collect — получил обратно (деньги пришли, долг человека уменьшился)
// Семантика долга: debtKind lend/return => человек должен мне (баланс +), borrow/collect => я должен (баланс -).

// id бывают и числами (с сервера, generateId), и строками (значения <select> в формах).
// Сравниваем по строковому представлению, чтобы оба случая работали.
const sameId = (a, b) => a != null && b != null && String(a) === String(b);

// Баланс обычного счёта (реальные деньги, включая переводы и долговые операции по этому счёту)
export function accountBalance(transactions, accountId) {
  return transactions.reduce((sum, t) => {
    if (t.type === 'transfer') {
      if (sameId(t.accountId, accountId)) return sum - (Number(t.amount) || 0);
      if (sameId(t.toAccountId, accountId)) return sum + (Number(t.amount) || 0);
      return sum;
    }
    if (t.type === 'debt') {
      if (!sameId(t.accountId, accountId)) return sum;
      const isIn = t.debtKind === 'borrow' || t.debtKind === 'collect';
      return sum + (isIn ? Number(t.amount) || 0 : -(Number(t.amount) || 0));
    }
    if (!sameId(t.accountId, accountId)) return sum;
    return sum + (t.type === 'income' ? (Number(t.amount) || 0) : -(Number(t.amount) || 0));
  }, 0);
}

// Баланс долга (человек): положительный = мне должны, отрицательный = я должен
export function debtBalance(transactions, debtAccountId) {
  return transactions.reduce((sum, t) => {
    if (t.type !== 'debt' || !sameId(t.debtAccountId, debtAccountId)) return sum;
    const plus = t.debtKind === 'lend' || t.debtKind === 'return';
    return sum + (plus ? Number(t.amount) || 0 : -(Number(t.amount) || 0));
  }, 0);
}

// Общий баланс: сумма балансов учитываемых счетов (не-долговых, с includeInBalance !== false)
export function totalBalance(accounts, transactions) {
  return (accounts || [])
    .filter(a => !a.isDebt && a.includeInBalance !== false)
    .reduce((sum, a) => sum + accountBalance(transactions || [], a.id), 0);
}

// Учитывается ли счёт в общем балансе
function included(accounts, id) {
  const acc = (accounts || []).find(a => sameId(a.id, id));
  return !!acc && !acc.isDebt && acc.includeInBalance !== false;
}

// Публичная проверка: учитывается ли счёт в общем балансе
// (нужно, чтобы операции со счетов вне баланса не влияли на суточный расчёт)
export function isIncludedAccount(accounts, id) {
  return included(accounts, id);
}

// Чистый эффект планируемых переводов на общий баланс.
// Перевод со счёта-в-балансе на счёт-не-в-балансе = деньги ушли (расход, -amount).
// Перевод со счёта-не-в-балансе на счёт-в-балансе = деньги пришли (доход, +amount).
// Оба в балансе или оба вне баланса = 0.
export function plannedTransfersNet(accounts, plannedTransfers) {
  return (plannedTransfers || []).reduce((sum, t) => {
    const fromIn = included(accounts, t.fromAccountId);
    const toIn = included(accounts, t.toAccountId);
    if (fromIn && !toIn) return sum - (Number(t.amount) || 0);
    if (!fromIn && toIn) return sum + (Number(t.amount) || 0);
    return sum;
  }, 0);
}

// Чистый эффект планируемых возвратов долгов на общий баланс.
// Возврат долга = деньги уходят с реального счёта (расход), если счёт в балансе.
export function plannedDebtReturnsNet(accounts, plannedDebtReturns) {
  return (plannedDebtReturns || []).reduce((sum, d) => {
    if (included(accounts, d.accountId)) return sum - (Number(d.amount) || 0);
    return sum;
  }, 0);
}

// ================= Планирование по категориям =================

// Нормализует планируемые доходы/расходы в группы по категориям.
// Новый формат группы: { category, amount (бюджет), finished, operations:[{id,amount,date,comment,done}] }
// Старый формат [{ category, amount, date }] превращается в операции (бюджет 0).
export function normPlanned(list) {
  const groups = [];
  const byCat = new Map();
  (list || []).forEach(item => {
    if (!item) return;
    const cat = item.category || '';
    let g = byCat.get(cat);
    if (!g) {
      g = { category: cat, amount: 0, finished: !!item.finished, operations: [] };
      byCat.set(cat, g);
      groups.push(g);
    }
    if (Array.isArray(item.operations)) {
      // новый формат: amount — бюджет группы, operations — разбивка на операции
      g.amount += Number(item.amount) || 0;
      g.finished = g.finished || !!item.finished;
      (item.operations || []).forEach((o, oi) => g.operations.push({
        id: o.id != null ? o.id : `n-${cat}-${oi}-${Number(o.amount) || 0}-${o.date || ''}`,
        amount: Number(o.amount) || 0,
        date: o.date || '',
        comment: o.comment || '',
        done: !!o.done,
        recurring: !!o.recurring,
        recurringId: o.recurringId || null,
      }));
    } else {
      // старый формат: сама строка — операция
      g.operations.push({
        id: `n-${cat}-${g.operations.length}-${Number(item.amount) || 0}-${item.date || ''}`,
        amount: Number(item.amount) || 0,
        date: item.date || '',
        comment: '',
        done: false,
      });
    }
  });
  // Миграция: раньше бюджет категории вводился вручную и мог храниться без операций.
  // Теперь бюджет складывается из запланированных платежей, поэтому ручной бюджет
  // превращаем в одну операцию, чтобы он не потерялся.
  groups.forEach(g => {
    if (g.operations.length === 0 && Number(g.amount) > 0) {
      g.operations.push({ id: `b-${g.category}`, amount: Number(g.amount), date: '', comment: '', done: false });
    }
  });
  return groups;
}

// Эффективная сумма группы:
// - незаконченная: бюджет или сумма всех операций, что больше
//   (пока операции в пределах бюджета, суточный остаток не меняется; превысили — учитывается всё);
// - законченная: учитываются только выполненные операции (остаток бюджета освобождается).
export function plannedGroupEffective(g) {
  const sum = (arr) => arr.reduce((s, o) => s + (Number(o.amount) || 0), 0);
  if (g.finished) {
    // законченная категория: учитываются только выполненные операции
    return sum((g.operations || []).filter(o => o.done));
  }
  // бюджет категории складывается из запланированных платежей (операций)
  return sum(g.operations || []);
}

// Итог по планируемым доходам/расходам с учётом эффективных сумм категорий.
// getActual — необязательная функция (category) => фактическая сумма по категории за период.
// Для завершённой категории учитывается фактическая сумма вместо плана, чтобы освободить
// неистраченный остаток: запланировали 10000, потратили 9000 → лишние 1000 возвращаются в бюджет.
export function totalPlanned(list, getActual) {
  return normPlanned(list).reduce((s, g) => {
    if (g.finished && getActual) {
      return s + (Number(getActual(g.category)) || 0);
    }
    return s + plannedGroupEffective(g);
  }, 0);
}

// Все операции планирования (для блока «Запланировано на сегодня»)
export function plannedOperations(list, type) {
  const ops = [];
  normPlanned(list).forEach(g => {
    (g.operations || []).forEach(o => ops.push({
      id: o.id,
      category: g.category,
      amount: o.amount,
      date: o.date,
      comment: o.comment,
      done: !!o.done,
      finished: !!g.finished,
      type,
      kind: type,
    }));
  });
  return ops;
}

// Остатки на счетах (учитываемых в общем балансе) на начало периода.
// Считаем по транзакциям ДО даты начала периода включительно (<=), иначе
// корректировки баланса, сделанные в день старта периода, не учитываются.
export function startingBalance(accounts, transactions, beforeDate) {
  const tx = (transactions || []).filter(t => !beforeDate || (t.date && t.date <= beforeDate));
  return totalBalance(accounts, tx);
}

// Остатки на счетах СТРОГО ДО даты (не включая её). Нужно для стартового остатка периода:
// операции в день старта считаются «в периоде» и не должны задваиваться с планом.
export function startingBalanceStrict(accounts, transactions, beforeDate) {
  const tx = (transactions || []).filter(t => !beforeDate || (t.date && t.date < beforeDate));
  return totalBalance(accounts, tx);
}

// ================= Связывание плана с фактом =================
// Помечает плановые операции, которые уже РЕАЛЬНО выполнены (в т.ч. вручную):
//  - доходы/расходы — покрыты фактическими операциями по категории (в порядке дат);
//  - переводы — есть реальный перевод с теми же счетами и суммой;
//  - возвраты долгов — есть реальная долговая операция с теми же счётом/человеком и суммой.
// Это нужно, чтобы:
//  1) суточный лимит не «задваивал» операцию, уже сделанную вручную (реальные деньги уже ушли,
//     а будущий план всё ещё резервирует её → лимит падает в 2 раза);
//  2) в плане операция, покрытая фактом, зачёркивалась как выполненная.
// Возвращает нормализованные планы с флагом effectiveDone на каждой операции.
export function effectivePlanned(period, categories, accounts, transactions, viewDate) {
  const tx = (transactions || []).filter(t => t.date && (!viewDate || t.date <= viewDate));
  const inPeriod = (t) => t.date && period && t.date >= period.startDate && t.date <= period.endDate;
  const catName = (id) => {
    const c = (categories || []).find(x => sameId(x.id, id));
    return c ? c.name : '';
  };

  // Фактические доходы/расходы по категориям (учтённые счета, внутри периода, до viewDate)
  const actualByCat = (type) => {
    const map = {};
    tx.filter(t => t.type === type && inPeriod(t) && included(accounts, t.accountId))
      .forEach(t => {
        const c = catName(t.categoryId);
        if (c) map[c] = (map[c] || 0) + (Number(t.amount) || 0);
      });
    return map;
  };
  const incomeActual = actualByCat('income');
  const expenseActual = actualByCat('expense');

  // Покрытие плановых операций категории фактом: операции «съедаются» фактической суммой по датам
  const coverOps = (groups, actualMap) => normPlanned(groups).map(g => {
    const sorted = (g.operations || []).map((o, i) => ({ o, i }))
      .sort((a, b) => (a.o.date || '').localeCompare(b.o.date || ''));
    let remaining = actualMap[g.category] || 0;
    const covered = new Set();
    for (const { o, i } of sorted) {
      if (remaining >= (Number(o.amount) || 0)) { covered.add(i); remaining -= Number(o.amount) || 0; }
      else break;
    }
    return {
      ...g,
      operations: (g.operations || []).map((o, i) => ({
        ...o,
        effectiveDone: !!o.done || covered.has(i),
      })),
    };
  });
  const incomes = coverOps(period && period.plannedIncomes, incomeActual);
  const expenses = coverOps(period && period.plannedExpenses, expenseActual);

  // Переводы: совпадают с реальными по (from|to|сумма) внутри периода
  const realTransfers = {};
  tx.filter(t => t.type === 'transfer' && inPeriod(t)).forEach(t => {
    const k = `${t.accountId}|${t.toAccountId}|${Math.round((Number(t.amount) || 0) * 100) / 100}`;
    realTransfers[k] = (realTransfers[k] || 0) + 1;
  });
  const transfers = (period && period.plannedTransfers || []).map(x => {
    const k = `${x.fromAccountId}|${x.toAccountId}|${Math.round((Number(x.amount) || 0) * 100) / 100}`;
    let eff = !!x.done;
    if (!eff && (realTransfers[k] || 0) > 0) { realTransfers[k]--; eff = true; }
    return { ...x, effectiveDone: eff };
  });

  // Возвраты долгов: совпадают только с реальным ВОЗВРАТОМ долга (debtKind='return',
  // деньги уходят) по (счёт|человек|сумма) внутри периода. Долги «взял в долг» (borrow,
  // деньги приходят) — это другая операция, они НЕ засчитываются как выполненный возврат.
  const realDebts = {};
  tx.filter(t => t.type === 'debt' && t.debtKind === 'return' && inPeriod(t)).forEach(t => {
    const k = `${t.accountId}|${t.debtAccountId}|${Math.round((Number(t.amount) || 0) * 100) / 100}`;
    realDebts[k] = (realDebts[k] || 0) + 1;
  });
  const debts = (period && period.plannedDebtReturns || []).map(d => {
    const k = `${d.accountId}|${d.debtAccountId}|${Math.round((Number(d.amount) || 0) * 100) / 100}`;
    let eff = !!d.done;
    if (!eff && (realDebts[k] || 0) > 0) { realDebts[k]--; eff = true; }
    return { ...d, effectiveDone: eff };
  });

  return { incomes, expenses, transfers, debts };
}

// ================= Динамический суточный лимит (база + перенос) =================
// Модель пользователя:
//   periodBudget  = стартовый остаток + плановые доходы − плановые расходы + переводы + долги
//                 (полный план периода — сумма, которую можно распределить на весь период)
//   baseDailyLimit = periodBudget / (все дни периода) — ФИКСИРОВАННЫЙ базовый лимит.
//     Он НЕ пересчитывается после каждой покупки («остаток ÷ оставшиеся дни» — так НЕ надо).
//   Каждый день к «переносу» (carry) добавляется baseDailyLimit и отклонение дня:
//     + получено сверх плана (доходы сверх бюджета категории/вне плана)
//     − потрачено сверх плана (расходы сверх бюджета категории/вне плана)
//     переводы/долги учитываются как реальные изменения доступных средств.
//   availableToday = carry на выбранную дату («Доступно сегодня»).
//   Недотраченное переносится на следующий день, перерасход уходит в минус
//   и компенсируется следующими днями (лимит при этом всегда одинаковый).
// Возвращает: { baseDailyLimit, availableToday, carryBeforeToday, periodBudget, totalDays, dailyDeviation }
export function dailyBudgetModel(period, categories, accounts, transactions, viewDate) {
  const startDate = period && period.startDate;
  const endDate = period && period.endDate;
  if (!startDate || !endDate) return null;

  const totalDays = Math.floor((new Date(endDate + 'T00:00:00') - new Date(startDate + 'T00:00:00')) / (1000 * 60 * 60 * 24)) + 1;
  if (totalDays <= 0) return null;

  // Полный плановый бюджет периода
  const planTotal = (list) => normPlanned(list).reduce((s, g) =>
    s + (g.operations || []).reduce((x, o) => x + (Number(o.amount) || 0), 0), 0);
  const planIncome = planTotal(period.plannedIncomes);
  const planExpense = planTotal(period.plannedExpenses);
  const planTransfersNet = plannedTransfersNet(accounts, period.plannedTransfers || []);
  const planDebtsNet = plannedDebtReturnsNet(accounts, period.plannedDebtReturns || []);
  const startBal = startingBalanceStrict(accounts, transactions, startDate);
  const periodBudget = startBal + planIncome - planExpense + planTransfersNet + planDebtsNet;
  const baseDailyLimit = periodBudget / totalDays;

  // Плановые бюджеты по категориям (суммы плановых операций)
  const planByCat = (list) => {
    const m = {};
    normPlanned(list).forEach(g => { m[g.category] = (m[g.category] || 0) + (g.operations || []).reduce((x, o) => x + (Number(o.amount) || 0), 0); });
    return m;
  };
  const incomePlan = planByCat(period.plannedIncomes);
  const expensePlan = planByCat(period.plannedExpenses);

  const sameIdLocal = (a, b) => a != null && b != null && String(a) === String(b);
  const catName = (cid) => { const c = (categories || []).find(x => sameIdLocal(x.id, cid)); return c ? c.name : ''; };

  // Фактические доходы/расходы по категориям за дату (учтённые счета).
  // «Без категории» (categoryId=null) тоже учитываем — как отдельную категорию вне плана,
  // чтобы доходы/расходы без категории влияли на «Доступно сегодня».
  const keyName = (cid) => { const n = catName(cid); return n || 'Без категории'; };
  const catActualOn = (type, date) => {
    const m = {};
    (transactions || []).forEach(t => {
      if (t.type !== type || t.date !== date || !included(accounts, t.accountId)) return;
      const n = keyName(t.categoryId);
      m[n] = (m[n] || 0) + (Number(t.amount) || 0);
    });
    return m;
  };
  // Фактические суммы по категориям ДО даты (для «осталось в плане категории»)
  const catActualBefore = (type, date) => {
    const m = {};
    (transactions || []).forEach(t => {
      if (t.type !== type || !(t.date && t.date < date) || !included(accounts, t.accountId)) return;
      const n = keyName(t.categoryId);
      m[n] = (m[n] || 0) + (Number(t.amount) || 0);
    });
    return m;
  };

  // Отклонение конкретного дня: (доходы/расходы сверх плана категории) + (вне плана — полностью)
  const dayDeviation = (date) => {
    let spent = 0;
    let inc = 0;
    const eToday = catActualOn('expense', date);
    const eBefore = catActualBefore('expense', date);
    Object.keys(eToday).forEach(c => {
      const plan = expensePlan[c];
      if (plan === undefined) { spent += eToday[c]; return; }
      const before = eBefore[c] || 0;
      const remaining = Math.max(0, plan - before);
      spent += Math.max(0, eToday[c] - remaining);
    });
    const iToday = catActualOn('income', date);
    const iBefore = catActualBefore('income', date);
    Object.keys(iToday).forEach(c => {
      const plan = incomePlan[c];
      if (plan === undefined) { inc += iToday[c]; return; }
      const before = iBefore[c] || 0;
      const remaining = Math.max(0, plan - before);
      inc += Math.max(0, iToday[c] - remaining);
    });
    return inc - spent;
  };

  // Переносим по дням от начала периода до viewDate включительно.
  // Даты форматируем ЛОКАЛЬНО (getFullYear/getMonth/getDate), а не через toISOString —
  // иначе из-за часового пояса день сдвигается назад и отклонения считаются не за тот день.
  const fmt = (dateObj) => {
    const m = String(dateObj.getMonth() + 1).padStart(2, '0');
    const dd = String(dateObj.getDate()).padStart(2, '0');
    return `${dateObj.getFullYear()}-${m}-${dd}`;
  };
  let carry = 0;
  let carryBeforeToday = 0;
  let dailyDeviation = 0;
  const cursor = new Date(startDate + 'T00:00:00');
  const stop = new Date(viewDate + 'T00:00:00');
  while (cursor <= stop) {
    const d = fmt(cursor);
    dailyDeviation = dayDeviation(d);
    if (d === viewDate) carryBeforeToday = carry;
    carry += baseDailyLimit + dailyDeviation;
    cursor.setDate(cursor.getDate() + 1);
  }

  return { baseDailyLimit, availableToday: carry, carryBeforeToday, periodBudget, totalDays, dailyDeviation };
}
